import { Box, Card, FormControlLabel, Stack, Switch, Typography } from "@mui/material";
import AccountReps from "./AccountReps";
import { useContext, useState } from "react";
import EditAccountReps from "../editPreferences/EditAccountReps";
import SalesReps from "./SalesReps";
import EditSalesReps from "../editPreferences/EditSalesReps";
import { ContactContext } from "../../contact/ContactContext";
import NoteCard from "../profile/NoteCard";
import DebugMenu from "./DebugMenu";
import DeactivateDialog from "./DeactivateDialog";
import { Contact, Note } from "../../types";
import axios from "axios";
import useDebugShortcut from "../../../utils/useDebugShortcut";
import { CompanyContext } from "../../company/CompanyContext";
import ProfilePostInput from "../../shared/profile/NoteInput";
import EditClientCultivator from "../editPreferences/EditClientCultivator";
import ClientCultivators from "./ClientCultivators";
import { UserContext } from "../../../contexts/UserContext";
import ContactFocusPreferences from "./ContactFocusPreferences";

export default function Preferences() {
  const { contact, setContact } = useContext(ContactContext);
  const { company } = useContext(CompanyContext);
  const { user } = useContext(UserContext);

  const [editSalesRepsMode, setEditSalesRepsMode] = useState<boolean>(false);
  const [editAccountRepsMode, setEditAccountRepsMode] = useState<boolean>(false);
  const [editClientCultivator, setEditClientCultivator] = useState<boolean>(false);
  const [isInDebugMode, setIsInDebugMode] = useState(false);

  const clientCultivatorSetters = [
    "agoldstein@madisoncres.com",
    "sdavidowitz@madisoncres.com",
    "clipschutz@madisoncres.com",
    "lhunger@madisontitle.com",
    "astrulowitz@madisontitle.com",
    "snierenberg@madisontitle.com",
    "jshapiro@madisontitle.com",
    "afriedman@madisoncres.com",
    "bfreylicher@madisoncres.com",
    "lbensoussan@madisoncres.com",
    "jshonek@madisontitle.com",
    "swohlberg@madisoncres.com",
    "ymeister@madisontitle.com",
    "yprag@madisontitle.com",
    "jsteinberg@madison1031.com",
    "esther@madison1031.com"
  ];

  const isClientCultivatorSetter = clientCultivatorSetters.includes(user?.email.toLowerCase() || "");

  useDebugShortcut(() => {
    setIsInDebugMode(true);
  });

  const handleIsLeadUpdate = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!contact?.id) return;
    const { data } = await axios.post<Contact>(`/api/clientphonebook/contacts/updateIsLead`, {
      id: contact.id,
      isLead: event.target.checked
    });
    if (data) {
      setContact((draft) => {
        if (draft) {
          draft.isLead = data.isLead;
        }
      });
    }
  };

  const handleActiveStatusUpdate = async (contactId: number, isActive: boolean) => {
    const { data } = await axios.post<Contact>(`/api/clientphonebook/contacts/updateActiveStatus`, {
      id: contactId,
      isActive
    });
    if (data) {
      setContact((draft) => {
        if (draft) {
          draft.isActive = data.isActive;
        }
      });
    }
  };
  const updateNotes = (note: Note) => {
    setContact((draft) => {
      if (draft) {
        draft.notes.unshift(note);
      }
    });
  };

  const saveNote = async (note: string) => {
    const { data } = await axios.post("/api/clientphonebook/contactNotes/add", {
      text: note,
      contactId: contact?.id,
      isClientInstructions: true
    });
    updateNotes(data);
  };

  const clientInstructions = contact?.notes.filter((n) => n.isClientInstructions) || [];

  const displayClientCultivatorInfo = () => {
    if (!isClientCultivatorSetter) return;

    if (editClientCultivator) {
      return <EditClientCultivator setEditMode={() => setEditClientCultivator((prevState) => !prevState)} />;
    }
    return <ClientCultivators setEditMode={() => setEditClientCultivator((prevState) => !prevState)} />;
  };

  return (
    <Box sx={{
      display: "grid",
      gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 2fr) minmax(0, 1fr)" },
      gap: 3,
      alignItems: "start"
    }}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" }, gap: 3 }}>
        {editAccountRepsMode ? (
          <EditAccountReps setEditMode={() => setEditAccountRepsMode((prevState) => !prevState)} />
        ) : (
          <AccountReps setEditMode={() => setEditAccountRepsMode((prevState) => !prevState)} />
        )}
        {editSalesRepsMode ? (
          <EditSalesReps setEditMode={() => setEditSalesRepsMode((prevState) => !prevState)} />
        ) : (
          <SalesReps setEditMode={() => setEditSalesRepsMode((prevState) => !prevState)} />
        )}
        {isClientCultivatorSetter && (
          <Box sx={{ gridColumn: "1 / -1" }}>{displayClientCultivatorInfo()}</Box>
        )}
        {contact && (
          <Box sx={{ gridColumn: "1 / -1", minWidth: 0 }}>
            <ContactFocusPreferences key={contact.id} contactId={contact.id} isActive={contact.isActive} />
          </Box>
        )}
        {isInDebugMode && contact && <Box sx={{ gridColumn: "1 / -1" }}><DebugMenu contactId={contact.id} /></Box>}
      </Box>

      <Stack component="section" aria-label="Client Instructions" spacing={3}>
        {clientInstructions.map((note) => (
          <NoteCard key={note.id} showClientInstructionsHeader note={note} companyId={company?.id} contactId={contact?.id} />
        ))}
        {clientInstructions.length === 0 && (
          <ProfilePostInput showClientInstructionsHeader saveNote={saveNote} updateNotes={updateNotes} />
        )}
        <Card>
          <Box sx={{ p: 2, display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Lookup code: <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>{contact?.lookupCode || "Not assigned"}</Box>
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <FormControlLabel
                control={<Switch size="small" checked={contact?.isLead ?? false} onChange={handleIsLeadUpdate} />}
                label="Lead"
              />
              <DeactivateDialog entityType="Contact" entity={contact} updateActiveStatus={handleActiveStatusUpdate} />
            </Stack>
          </Box>
        </Card>
      </Stack>
    </Box>
  );
}
